import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Article } from '@wix/answers-api';

import { renderModel } from '../../util/renderModel';
import { CategoryItemType } from '../api/getCategories';
import { IHelpCenterState, PresentedState } from './types';

const isFullWidth = renderModel.isFullWidth();

const initialState: IHelpCenterState = {
  currentCategory: null,
  currentArticle: null,
  currentArticleLoading: false,
  categories: [],
  categoryArticles: [],
  normalizedCategories: {},
  articleHeadingVisible: false,
  fullPageSidebarOpened: false,
  fullPageSidebarExtended: false,
  searchedText: '',
  currentPresentedState: undefined,
  // if it is Full Page(Contact page) there is no previous state.
  // Otherwise, users see Widget Button(Anchor Button or Help Button in the header)
  previousPresentedState: isFullWidth ? undefined : PresentedState.WidgetButton,
};

const getNormalizedCategories = (
  categories: CategoryItemType[],
): Record<CategoryItemType['id'], CategoryItemType> => {
  let categoriesRecord: Record<CategoryItemType['id'], CategoryItemType> = {};

  (categories ?? []).forEach((category) => {
    categoriesRecord[category.id] = category;

    if (category.children) {
      categoriesRecord = {
        ...categoriesRecord,
        ...getNormalizedCategories(category.children),
      };
    }
  });

  return categoriesRecord;
};

export const helpCenterSlice = createSlice({
  name: 'helpCenter',
  initialState,
  reducers: {
    setCategories: (state, action) => {
      state.categories = action.payload ?? [];
      state.normalizedCategories = getNormalizedCategories(action.payload);
    },
    setCategoryArticles: (state, action: PayloadAction<Article[]>) => {
      state.categoryArticles = action.payload;
    },
    addCategoryArticles: (state, action: PayloadAction<Article[]>) => {
      state.categoryArticles = [...state.categoryArticles, ...action.payload];
    },
    setCurrentCategory: (state, action) => {
      state.currentArticle = null;
      state.currentCategory = action.payload;
    },
    setCurrentArticle: (state, action) => {
      state.currentCategory = null;
      state.currentArticle = action.payload;
    },
    setCurrentArticleLoading: (state, action) => {
      state.currentArticleLoading = action.payload;
    },
    setArticleHeadingVisible: (state, action) => {
      state.articleHeadingVisible = action.payload;
    },
    setSidebarOpened: (state, action: PayloadAction<boolean>) => {
      state.fullPageSidebarOpened = action.payload;
    },
    setSidebarExtended: (state, action: PayloadAction<boolean>) => {
      state.fullPageSidebarExtended = action.payload;
    },
    setSearchedText: (state, action: PayloadAction<string>) => {
      state.searchedText = action.payload;
    },
    setPresentedState: (state, action: PayloadAction<PresentedState>) => {
      // if current state is presented set it as a previous state
      if (state.currentPresentedState) {
        state.previousPresentedState = state.currentPresentedState;
      }
      state.currentPresentedState = action.payload;
    },
  },
});
